import { configureStore, ThunkDispatch } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import { Action } from 'redux';
import { decodeAPITokenJWT } from 'fym-common/src/utils/decodeAPITokenJWT';
import { Music24JWTUser } from 'fym-common/src/db/userTypes';
import { Environment } from 'fym-common/src/config/types';
import { config } from '../config/config';
import { RootState } from './root/state';
import { rootReducer } from './root/reducer';
import { fymAPI } from './api/fymAPI';
import { musicAPI } from './api/musicAPI';
import { appInfoSliceName } from './slices/appInfo/constants';
import { SentryReact } from '../modules/errorBoundary/sentry';
import { readStoredTourActive } from '../modules/onboarding/tour/tourSession';

export const createReduxStore = ({ deviceId, userToken }: { deviceId: string; userToken: string | null }) => {
  console.log(`DeviceID: ${deviceId}`);

  const isProduction = config.environment === Environment.production;
  const userTokenDecoded = decodeAPITokenJWT<Music24JWTUser>(userToken);
  const sentryReduxEnhancer = SentryReact.createReduxEnhancer({
    stateTransformer: () => null,
    actionTransformer: (action) => ({ type: action.type }),
  });
  const store = configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) => {
      return getDefaultMiddleware({ serializableCheck: false }).concat(fymAPI.middleware, musicAPI.middleware);
    },
    enhancers: (getDefaultEnhancers) => getDefaultEnhancers().concat(sentryReduxEnhancer),
    preloadedState: {
      ...RootState,
      appInfo: {
        ...RootState[appInfoSliceName],
        deviceId,
        userToken,
        userTokenDecoded,
        // A reload keeps the guide bar of the LaziiBee tour; the step comes from the server.
        tourActive: userToken !== null && readStoredTourActive(),
      },
    },
    devTools: !isProduction,
  });
  setupListeners(store.dispatch);
  return store;
};

export type StoreType = ReturnType<typeof createReduxStore> & {
  dispatch: ThunkDispatch<ReturnType<typeof createReduxStore>['getState'], any, Action>;
};

export type RootStateType = ReturnType<StoreType['getState']>;
